import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import SignIn from './components/auth/SignIn';
import Home from './components/pages/Home';
import Homie from './pages/home/Home';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import Profile from './components/pages/Profile';
import Navbar from './pages/nav/Navbar';
import Footer from './components/layout/Footer';
import Pricing from './pages/Pricing';
import EmailVerification from './pages/product/EmailVerification';
import BulkValidation from './pages/product/BulkValidation';
import ApiDocs from './pages/product/ApiDocs';
import Marketing from './pages/solutions/Marketing';
import SaaS from './pages/solutions/SaaS';
import Agencies from './pages/solutions/Agencies';
import CRMs from './pages/solutions/CRMs';
import Security from './pages/company/Security';
import Contact from './components/company/Contact';
import SignUp from './components/auth/SignUp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const COUNTRY_TO_CURRENCY = {
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  UK: 'GBP',
  IN: 'INR',
  AU: 'AUD',
  NZ: 'AUD',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
};

const DEFAULT_CURRENCY = 'USD';

const getDefaultCurrency = () => {
  if (typeof navigator !== 'undefined') {
    const locale = navigator.language || 'en-US';
    const region = locale.split('-')[1];
    if (region && COUNTRY_TO_CURRENCY[region.toUpperCase()]) {
      return COUNTRY_TO_CURRENCY[region.toUpperCase()];
    }
  }
  return DEFAULT_CURRENCY;
};

function App() {
  const [authToken, setAuthToken] = useState(() =>
    localStorage.getItem('authToken')
  );
  const [user, setUser] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [pricingCurrency, setPricingCurrency] = useState(getDefaultCurrency());

  const isAuthenticated = Boolean(authToken);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    [authToken]
  );

  const handleAuthRequired = () => {
    toast.info("Please sign in to continue");
    window.location.href = "/signin";
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  useEffect(() => {
    if (!authToken) {
      return;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const response = await axios.get(`${API}/user/profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!cancelled) {
          setUser(response.data);
          if (response.data?.currency_preference) {
            setPricingCurrency(response.data.currency_preference);
          }
        }
      } catch (error) {
        console.error('Unable to load profile:', error);
        if (error?.response?.status === 401) {
          localStorage.removeItem('authToken');
          setAuthToken(null);
          setUser(null);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [authToken]);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
      <BrowserRouter>
        <Navbar user={user} isAuthenticated={isAuthenticated} />
        <Routes>
          <Route
            path="/signin"
            element={
              <PublicRoute>
                <SignIn
                  setAuthToken={setAuthToken}
                  setUser={setUser}
                  setStatusMessage={setStatusMessage}
                  pricingCurrency={pricingCurrency}
                />
              </PublicRoute>
            }
          />


                <Route
            path="/signup"
            element={
              <PublicRoute>
                <SignUp
                  setAuthToken={setAuthToken}
                  setUser={setUser}
                  setStatusMessage={setStatusMessage}
                  pricingCurrency={pricingCurrency}
                />
              </PublicRoute>
            }
          />


          <Route
            path="/homie"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Homie />} />
          <Route path="/product/api" element={<ApiDocs />} />
          <Route path="/company/contact" element={<Contact />} />


          <Route
            path="/pricing"
            element={
              <Pricing
                user={user}
                authToken={authToken}
                onAuthRequired={handleAuthRequired}
                onUserUpdate={handleUserUpdate}
              />
            }
          />

          <Route
            path="/product/email-validation"
            element={
              <EmailVerification
                user={user}
                authToken={authToken}
              />
            }
          />
          <Route
            path="/product/bulk-validation"
            element={
              <BulkValidation
                user={user}
                authToken={authToken}
              />
            }
          />

          <Route
            path="/solutions/marketing"
            element={
              <Marketing
                user={user}
                authToken={authToken}
              />
            }
          />

          <Route
            path="/solutions/saas"
            element={<SaaS user={user} authToken={authToken} />}
          />
          <Route
            path="/solutions/agencies"
            element={<Agencies user={user} authToken={authToken} />}
          />
          <Route
            path="/solutions/crms"
            element={<CRMs user={user} authToken={authToken} />}
          />
          <Route
            path="/company"
            element={<Security user={user} authToken={authToken} />}
          />

        </Routes>
        <Footer />
      </BrowserRouter>
    </>
  );
}

export default App;
